import { RouterInputs, RouterOutputs } from '@crocodille/api';
import {
  assert,
  assertSome,
  Cell,
  isNone,
  isSome,
  Maybe,
  noop,
  useCellValue,
} from '@crocodille/util';
import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import useSWR, { mutate, unstable_serialize, useSWRConfig } from 'swr';
import useSWRMutation, { SWRMutationConfiguration } from 'swr/mutation';
import { create as createStore } from 'zustand';
import { shallow } from 'zustand/shallow';

import { App } from '../app';
import { useApp } from '../app-provider';
import {
  v2_OrderCompleteFormData,
  v2_useOrderCompleteForm,
} from '../forms/order-complete-form.v2';
import { AppError, AppErrorType } from '../util/error';
import { laggy } from '../util/swr';
import { useAuthorizedCustomer } from './auth.v2';
import { useCustomerBillingAddress } from './customer.v2';
import { useStore } from './key-value-store';
import { getOrderKey } from './order.v2';
import { usePaymentMethods } from './payment';
import {
  isSingleChoiceProperty,
  isFreeformProperty,
  ListProduct,
  ProductDetail,
  ProductDetailState,
  selectMultipleChoiceConfigurationPropertyValue,
  selectSingleChoiceConfigurationPropertyValue,
} from './products.v2';

export const CART_SESSION_KEY = 'cart';

/**
 * Order identifier is composed of:
 * - tokenValue
 * - branchCode
 *
 * Serialized as string in format: `${tokenValue};${branchCode}`
 */
export interface ParsedOrderIdentifier {
  /**
   * Token value of the order.
   */
  readonly tokenValue: string;
  /**
   * Branch code of the order.
   */
  readonly branchCode: string;
}

export function parseOrderIdentifier(
  orderIdentifier: string,
): ParsedOrderIdentifier | null {
  const parts = orderIdentifier.split(';');

  if (parts.length !== 2) {
    return null;
  }

  const [tokenValue, branchCode] = parts;

  return {
    tokenValue,
    branchCode,
  };
}

export function serializeOrderIdentifier(
  orderIdentifier: ParsedOrderIdentifier,
): string {
  if (
    isNone(orderIdentifier.tokenValue) ||
    isNone(orderIdentifier.branchCode)
  ) {
    return '';
  }

  return [orderIdentifier.tokenValue, orderIdentifier.branchCode].join(';');
}

export function getCartOrderIdentifier(app: App) {
  const storedOrderIdentifier = app.session.read<string>(CART_SESSION_KEY);

  if (!storedOrderIdentifier) {
    return null;
  }

  return parseOrderIdentifier(storedOrderIdentifier);
}

export function useCartOrderIdentifier() {
  const app = useApp();

  const [storedOrderIdentifier, setStoredOrderIdentifier] = useStore<string>(
    app.session,
    CART_SESSION_KEY,
  );

  const setOrderIdentifier = useCallback(
    (value: Maybe<ParsedOrderIdentifier>) => {
      setStoredOrderIdentifier(
        value ? serializeOrderIdentifier(value) : undefined,
      );
    },
    [setStoredOrderIdentifier],
  );

  const orderIdentifier = storedOrderIdentifier
    ? parseOrderIdentifier(storedOrderIdentifier)
    : undefined;

  return [orderIdentifier, setOrderIdentifier] as const;
}

export function getItemCountInCart(
  cart?: RouterOutputs['order']['byTokenValue'] | null,
): number {
  if (!cart) {
    return 0;
  }

  return cart.items.reduce((acc, item) => acc + item.quantity, 0);
}

function getCartKey(
  orderIdentifierOrTokenValue: Maybe<ParsedOrderIdentifier> | string,
) {
  if (isNone(orderIdentifierOrTokenValue)) {
    return false;
  }

  if (typeof orderIdentifierOrTokenValue === 'string') {
    return ['cart', orderIdentifierOrTokenValue] as const;
  }

  return ['cart', orderIdentifierOrTokenValue.tokenValue] as const;
}

type CartKey = ReturnType<typeof getCartKey>;

// Optimized hooks without re-renders
export function useCartItemsCount() {
  return useCellValue(CART_ITEMS_COUNT);
}
export function useCartIsEmpty() {
  return useCellValue(CART_IS_EMPTY);
}

const CART_ITEMS_COUNT = new Cell<number>(0);
const CART_IS_EMPTY = new Cell<boolean>(true);

export function useCart() {
  const { trpc } = useApp();
  const [orderIdentifier, setCartOrderIdentifier] = useCartOrderIdentifier();

  const cartResponse = useSWR(
    getCartKey(orderIdentifier),
    async ([_, tokenValue]) => {
      if (!tokenValue) {
        return null;
      }

      const order = await trpc.order.byTokenValue.query(tokenValue);

      return order;
    },
    {
      revalidateOnMount: true,
      revalidateOnFocus: true,
      onSuccess(data) {
        if (isNone(data) && isSome(orderIdentifier)) {
          setCartOrderIdentifier(null);
        }
      },
    },
  );

  useEffect(() => {
    const newCount = getItemCountInCart(cartResponse.data);
    if (newCount !== CART_ITEMS_COUNT.value) {
      CART_ITEMS_COUNT.value = newCount;

      const newCartIsEmpty = newCount < 1;
      if (newCartIsEmpty !== CART_IS_EMPTY.value) {
        CART_IS_EMPTY.value = newCartIsEmpty;
      }
    }
  }, [cartResponse.data]);

  const clearCart = useCallback(() => {
    setCartOrderIdentifier(null);
  }, [setCartOrderIdentifier]);

  return { ...cartResponse, clearCart };
}

export async function fetchCart(app: App) {
  const orderIdentifier = getCartOrderIdentifier(app);

  if (!orderIdentifier) {
    return null;
  }

  const cart = await app.trpc.order.byTokenValue.query(
    orderIdentifier.tokenValue,
  );

  return cart;
}

export async function asCartFallback(
  app: App,
  cart: RouterOutputs['order']['byTokenValue'],
) {
  const orderIdentifier = getCartOrderIdentifier(app);

  if (!cart) {
    return null;
  }

  return {
    [unstable_serialize(getCartKey(orderIdentifier))]: cart,
  };
}

export async function fetchCartFallback(app: App) {
  return asCartFallback(app, await fetchCart(app));
}

export function useCreateNewCart(
  onCartCreated?: (order: RouterOutputs['order']['create']) => void,
  { onError }: { onError?: (error: unknown) => void } = {},
) {
  const app = useApp();
  const { locale, trpc } = app;

  const [_, setOrderIdentifier] = useCartOrderIdentifier();
  const pendingCartRef = useRef<
    Omit<RouterInputs['order']['create'], 'locale'> | undefined
  >(undefined);

  const [
    bufferedConfiguration,
    clearBuffered,
    setBufferedError,
    bufferedError,
  ] = useAddToCartBuffer(
    (store) => [store.bufferedItem, store.clear, store.setError, store.error],
    shallow,
  );

  const [localError, setLocalError] = useState<Error | null>(null);

  const createNewCartMutation = useSWRMutation(
    'createNewCart',
    async (
      _,
      {
        arg,
      }: {
        arg: Omit<RouterInputs['order']['create'], 'locale'>;
      },
    ) => {
      pendingCartRef.current = arg;
      setLocalError(null);

      let order = await trpc.order.create.mutate({
        locale,
        ...arg,
      });

      if (isSome(bufferedConfiguration) && isNone(bufferedError)) {
        //
        // If there was a buffered configuration, add it to the cart.
        //
        const result = await trpc.order.addItem.mutate({
          tokenValue: order.tokenValue,
          configuration: bufferedConfiguration,
        });

        if (result.ok) {
          order = result.data;
        } else {
          const e = AppError.from(result.error);

          if (
            e.kind === 'product_unavailable_on_branch' ||
            e.kind === 'product_unavailable'
          ) {
            setBufferedError(e);
          }

          throw e;
        }

        clearBuffered(true);
      }

      return order;
    },
    {
      onSuccess: (order) => {
        pendingCartRef.current = undefined;

        setOrderIdentifier({
          tokenValue: order.tokenValue,
          branchCode: order.branchCode,
        });

        onCartCreated?.(order);
        clearBuffered(true);
      },
      onError: (e) => {
        pendingCartRef.current = undefined;
        setLocalError(e);
        onError?.(e as unknown);
      },
      populateCache: true,
    },
  );

  return {
    ...createNewCartMutation,
    pendingCart: pendingCartRef.current,
    newCartError: localError,
    resetNewCartError: () => setLocalError(null),
  };
}

export function useAddToCart(product: ListProduct) {
  const canAddWithoutConfiguration = product.configurationStyle === null;

  const { trigger, ...addToCartMutation } = useAddToCartMutation();

  const variants = product.variants;

  const addVariantToCart = useCallback(
    async (variantCode?: string) => {
      if (!canAddWithoutConfiguration) {
        return false;
      }

      if (!variantCode && variants) {
        variantCode = variants.find((v) => v.default)?.code;
      }

      if (!variantCode) {
        return false;
      }

      const added = await trigger({
        configuration: [
          { role: 'variant', itemCode: variantCode, quantity: 1 },
        ],
        productSlug: product.slug,
      });

      return added;
    },
    [canAddWithoutConfiguration, variants, trigger],
  );

  return {
    addVariantToCart,
    ...addToCartMutation,
    canAddWithoutConfiguration,
  };
}

function validateProductConfiguration(
  {
    maxSelectedConfigurationOptions,
    filteredConfiguration: configuration,
    configurationStyle,
  }: {
    readonly maxSelectedConfigurationOptions: ProductDetail['maxSelectedConfigurationOptions'];
    readonly filteredConfiguration: ProductDetail['configuration'];
    readonly configurationStyle: ProductDetail['configurationStyle'];
  },
  configurationValues: ProductDetailState['configurationValues'],
) {
  const errors = [];

  let totalSelectedOptions = 0;

  for (const property of configuration) {
    if (isSingleChoiceProperty(property)) {
      const propertyValue = selectSingleChoiceConfigurationPropertyValue(
        configurationValues,
        property.id,
      );

      if (property.minSelectedOptions > 0 && property.options.length > 0) {
        //
        // Validate mandatory properties
        //
        if (propertyValue === null) {
          errors.push({
            propertyId: property.id,
            kind: 'required' as const,
          });
        }
      } else {
        //
        // We only count not-mandatory ingredients as selected options.
        //
        if (isSome(propertyValue) && property.role === 'ingredient') {
          totalSelectedOptions += 1;
        }
      }
    } else {
      const propertyValue = selectMultipleChoiceConfigurationPropertyValue(
        configurationValues,
        property.id,
      );

      const totalSelected = Object.values(propertyValue)
        .filter(isSome)
        .reduce((acc, quantity) => acc + quantity, 0);

      //
      // Add selected ingredients to total selected options
      //
      if (property.role === 'ingredient' || property.role === 'half') {
        totalSelectedOptions += totalSelected;
      }

      if (totalSelected < property.minSelectedOptions) {
        errors.push({
          propertyId: property.id,
          kind: 'min_selected_options' as const,
        });
      } else if (
        isSome(property.maxSelectedOptions) &&
        totalSelected > property.maxSelectedOptions
      ) {
        errors.push({
          propertyId: property.id,
          kind: 'max_selected_options' as const,
        });
      }
    }
  }

  if (
    isSome(maxSelectedConfigurationOptions) &&
    totalSelectedOptions > maxSelectedConfigurationOptions
  ) {
    errors.push({
      propertyId: null, // Not assigned to specific property
      kind: 'max_total_options' as const,
    });
  }

  const divisions = configuration.find(
    (property) => property.role === 'half',
  )?.maxSelectedOptions;

  if (
    isSome(divisions) &&
    configurationStyle === 'configurator_divided' &&
    totalSelectedOptions !== divisions
  ) {
    errors.push({
      propertyId: null, // Not assigned to specific property
      kind: 'division_count' as const,
    });
  }

  return errors;
}

export type AddConfigurationToCartError = ReturnType<
  typeof validateProductConfiguration
>[number];

function useAddToCartMutation() {
  const { trpc } = useApp();
  const { data: cart } = useCart();

  const cartRef = useRef(cart);
  cartRef.current = cart;

  const bufferConfiguration = useAddToCartBuffer((state) => state.buffer);

  const { trigger: triggerInternal, ...mutation } = useSWRMutation<
    | Extract<RouterOutputs['order']['addItem'], { readonly ok: true }>['data']
    | null,
    AppErrorType<
      Extract<
        RouterOutputs['order']['addItem'],
        { readonly ok: false }
      >['error']
    >,
    CartKey,
    {
      configuration: RouterInputs['order']['addItem']['configuration'];
      productSlug?: string;
    }
  >(
    getCartKey(cart?.tokenValue),
    async (_, { arg: { configuration, productSlug } }) => {
      if (cart) {
        const result = await trpc.order.addItem.mutate({
          tokenValue: cart.tokenValue,
          configuration,
          productSlug,
        });

        if (result.ok) {
          return result.data;
        } else {
          throw AppError.from(result.error);
        }
      } else {
        return null;
      }
    },
    {
      populateCache: true,
      revalidate: true,
    },
  );

  const trigger = useCallback(
    async (arg: {
      configuration: RouterInputs['order']['addItem']['configuration'];
      productSlug?: string;
    }) => {
      if (!cartRef.current) {
        bufferConfiguration(arg.configuration);

        return isOrderCreatedWhenBufferCleared();
      } else {
        const orderInCart = await triggerInternal(arg);

        return isSome(orderInCart);
      }
    },
    [triggerInternal],
  );

  return { trigger, ...mutation };
}

export function calculateConfigurationPrice(
  configuration: ProductDetail['configuration'],
  configurationValues: ProductDetailState['configurationValues'],
): number {
  return configuration.reduce((acc, property) => {
    if (isSingleChoiceProperty(property)) {
      const propertyValue = selectSingleChoiceConfigurationPropertyValue(
        configurationValues,
        property.id,
      );

      const option = property.options.find((o) => o.itemCode === propertyValue);

      if (!option) {
        return acc;
      }

      return acc + (option.unitPrice ?? 0);
    } else {
      const propertyValue = selectMultipleChoiceConfigurationPropertyValue(
        configurationValues,
        property.id,
      );

      return (
        acc +
        property.options.reduce((acc, option) => {
          assert(isNone(propertyValue) || typeof propertyValue === 'object');
          const quantity = propertyValue?.[option.itemCode] ?? 0;

          return acc + quantity * (option.unitPrice ?? 0);
        }, 0)
      );
    }
  }, 0);
}

function configurationValuesAsAddToCardPayload(
  product: ProductDetail,
  configurationValues: ProductDetailState['configurationValues'],
): RouterInputs['order']['addItem']['configuration'] {
  const configuration: RouterInputs['order']['addItem']['configuration'] = [];

  for (const property of product.configuration) {
    const value = configurationValues[property.id];

    if (isNone(value)) {
      continue;
    }

    if (isFreeformProperty(property)) {
      assert(value == null || typeof value === 'string');

      configuration.push({
        role: property.role,
        itemCode: property.id,
        quantity: 1,
        value,
      });
    } else if (isSingleChoiceProperty(property)) {
      assert(typeof value === 'string');

      configuration.push({
        role: property.role,
        itemCode: value,
        quantity: 1,
      });
    } else {
      assert(typeof value === 'object');

      for (const [itemCode, quantity] of Object.entries(value)) {
        // * Intentional check for null or undefined

        if (isNone(quantity)) {
          continue;
        }

        configuration.push({
          role: property.role,
          itemCode,
          quantity,
        });
      }
    }
  }

  return configuration;
}

export function useAddConfigurationToCart({
  product,
  filteredConfiguration,
  configurationValues,
}: {
  product: ProductDetail | null | undefined;
  filteredConfiguration: ProductDetail['configuration'];
  configurationValues:
    | ProductDetailState['configurationValues']
    | null
    | undefined;
}) {
  const { trigger, ...addToCartMutation } = useAddToCartMutation();

  const addConfigurationToCart = useCallback(async () => {
    if (!product || !configurationValues) {
      return false;
    }

    return trigger({
      configuration: configurationValuesAsAddToCardPayload(
        product,
        configurationValues,
      ),
      productSlug: product.slug,
    });
  }, [trigger, product, configurationValues]);

  const configuration = product?.configuration;

  const totalPrice = useMemo(() => {
    if (!configuration || !configurationValues) {
      return undefined;
    }

    return calculateConfigurationPrice(configuration, configurationValues);
  }, [configuration, configurationValues]);

  const configurationValidationErrors = useMemo(() => {
    if (
      product?.configurationStyle === undefined ||
      product?.maxSelectedConfigurationOptions === undefined ||
      !filteredConfiguration ||
      !configurationValues
    ) {
      return [];
    }

    return validateProductConfiguration(
      {
        configurationStyle: product?.configurationStyle,
        maxSelectedConfigurationOptions:
          product?.maxSelectedConfigurationOptions,
        filteredConfiguration,
      },
      configurationValues,
    );
  }, [
    product?.configurationStyle,
    product?.maxSelectedConfigurationOptions,
    filteredConfiguration,
    configurationValues,
  ]);

  return {
    totalPrice,
    configurationValidationErrors,
    canAddToCart:
      Boolean(product?.available) &&
      configurationValidationErrors?.length === 0,
    addConfigurationToCart,
    ...addToCartMutation,
  };
}

export function useRemoveFromCart() {
  const { trpc } = useApp();
  const [orderIdentifier] = useCartOrderIdentifier();

  const { trigger, ...removeFromCartMutation } = useSWRMutation(
    getCartKey(orderIdentifier),
    async (
      [_, tokenValue],
      {
        arg: { orderItemId },
      }: {
        arg: {
          orderItemId: number;
        };
      },
    ) => {
      const order = await trpc.order.removeItem.mutate({
        tokenValue,
        itemId: orderItemId,
      });

      return order;
    },
    {
      populateCache: true,
      revalidate: false,
    },
  );

  const removeFromCart = useCallback(
    ({ orderItemId }: { orderItemId: number }) =>
      trigger(
        { orderItemId },
        {
          optimisticData: (currentData) => {
            assert(isSome(currentData));

            const itemToRemove = currentData.items.find(
              (item) => item.id === orderItemId,
            );

            if (!itemToRemove) {
              return currentData;
            }

            return {
              ...currentData,
              items: currentData.items.filter(
                (item) => item.id !== orderItemId,
              ),
              total: currentData.total - itemToRemove?.price,
            };
          },
        },
      ),
    [trigger],
  );

  return { trigger: removeFromCart, ...removeFromCartMutation };
}

export function useRemoveFromCartByCodes(
  options?: SWRMutationConfiguration<
    RouterOutputs['order']['removeItemsByCode'],
    Error,
    readonly string[],
    ReturnType<typeof getCartKey>
  >,
) {
  const { trpc } = useApp();
  const [orderIdentifier] = useCartOrderIdentifier();

  const { trigger, ...removeFromCartMutation } = useSWRMutation(
    getCartKey(orderIdentifier),
    async ([_, tokenValue], { arg: itemCodes }) => {
      const order = await trpc.order.removeItemsByCode.mutate({
        itemCodes: [...itemCodes],
        tokenValue,
      });

      return order;
    },
    {
      populateCache: true,
      revalidate: true,
      ...options,
    },
  );

  const removeFromCartByCodes = useCallback(
    async (itemCodes: readonly string[]) => {
      const order = await trigger(itemCodes, {
        optimisticData: (currentData) => {
          assert(isSome(currentData));

          const itemsToRemove = currentData.items.filter((item) =>
            itemCodes.includes(item.code),
          );

          if (itemsToRemove.length === 0) {
            return currentData;
          }

          return {
            ...currentData,
            items: currentData.items.filter(
              (item) => !itemCodes.includes(item.code),
            ),
            total:
              currentData.total -
              itemsToRemove?.reduce?.((acc, item) => {
                return acc + item.price;
              }, 0),
          };
        },
      });

      await mutate(getCartKey(orderIdentifier));

      return order;
    },
    [trigger, mutate, orderIdentifier],
  );

  return { trigger: removeFromCartByCodes, ...removeFromCartMutation };
}

export function useChangeItemQuantity() {
  const { trpc } = useApp();
  const [orderIdentifier] = useCartOrderIdentifier();

  const { trigger, ...changeItemQuantityMutation } = useSWRMutation(
    getCartKey(orderIdentifier),
    async (
      [_, tokenValue],
      {
        arg: { orderItemId, quantity },
      }: {
        arg: {
          orderItemId: number;
          quantity: number;
        };
      },
    ) => {
      const order = await trpc.order.changeItemQuantity.mutate({
        tokenValue,
        itemId: orderItemId,
        quantity,
      });

      return order;
    },
    {
      populateCache: true,
    },
  );

  function getOptimisticData(
    currentData: RouterOutputs['order']['changeItemQuantity'],
    orderItemId: number,
    quantity: number,
  ): RouterOutputs['order']['changeItemQuantity'] {
    const itemToChange = currentData.items.find(
      (item) => item.id === orderItemId,
    );

    if (!itemToChange) {
      return currentData;
    }

    const oldItemPrice = itemToChange.price;
    const newItemPrice =
      (itemToChange.price / itemToChange.quantity) * quantity;

    return {
      ...currentData,
      items: currentData.items.map((item) => {
        if (item.id === orderItemId) {
          return {
            ...item,
            quantity,
            adjustments: item.adjustments.map((adjustment) => {
              if (isSome(adjustment.quantity)) {
                return {
                  ...adjustment,
                  quantity,
                  price: (adjustment.price / adjustment.quantity) * quantity,
                };
              }

              return adjustment;
            }),
            price: newItemPrice,
          };
        }

        return item;
      }),
      total: currentData.total - oldItemPrice + newItemPrice,
    };
  }

  const changeItemQuantity = useCallback(
    ({ orderItemId, quantity }: { orderItemId: number; quantity: number }) =>
      trigger(
        { orderItemId, quantity },
        {
          optimisticData: (currentData) => {
            assert(isSome(currentData));

            return getOptimisticData(currentData, orderItemId, quantity);
          },
        },
      ),
    [trigger],
  );

  return { trigger: changeItemQuantity, ...changeItemQuantityMutation };
}

export function useCartPaymentMethods() {
  const { trpc, locale } = useApp();
  const { data: cart } = useCart();

  const paymentMethodsResponse = useSWR(
    cart ? ['order', cart.tokenValue, 'paymentMethods'] : false,
    async () => {
      if (!cart) {
        return undefined;
      }

      const paymentMethods = await trpc.order.paymentMethods.query({
        tokenValue: cart.tokenValue,
        localeCode: locale,
      });

      return paymentMethods;
    },
  );

  return paymentMethodsResponse;
}

export function useCartSpecialDeliveryPrice(
  targetSpecialDelivery?: RouterInputs['order']['specialDeliveryPrice']['specialDelivery'],
) {
  const { trpc } = useApp();
  const { data: cart } = useCart();

  const specialDeliveryPriceResponse = useSWR(
    cart
      ? ['cart', cart.tokenValue, 'specialDeliveryPrice', targetSpecialDelivery]
      : null,
    async () => {
      if (!cart) {
        return undefined;
      }

      const specialDeliveryPrice = await trpc.order.specialDeliveryPrice.query({
        tokenValue: cart.tokenValue,
        specialDelivery: targetSpecialDelivery,
      });

      return specialDeliveryPrice;
    },
  );

  return specialDeliveryPriceResponse;
}

export function useCartExpectedPickupTimes() {
  const { trpc } = useApp();
  const { data: cart } = useCart();

  const expectedPickupTimesResponse = useSWR(
    cart?.deliveryMethodType === 'atBranch'
      ? (['order.expectedPickupTimes', cart.tokenValue] as const)
      : false,
    async ([_, tokenValue]) => {
      const expectedPickupTimes = await trpc.order.expectedPickupTimes.query({
        tokenValue,
      });

      return expectedPickupTimes;
    },
    {
      refreshInterval: 60 * 1000, // 1 minute
      revalidateOnFocus: true,
      revalidateOnMount: true,
      use: [laggy],
    },
  );

  return expectedPickupTimesResponse;
}

export function useCartScheduledDeliveryTimes() {
  const { trpc } = useApp();
  const { data: cart } = useCart();

  const expectedPickupTimesResponse = useSWR(
    cart?.deliveryMethodType === 'courier'
      ? (['order.scheduledDeliveryTimes', cart.tokenValue] as const)
      : false,
    async ([_, tokenValue]) => {
      const scheduledDeliveryTimes =
        await trpc.order.scheduledDeliveryTimes.query({
          tokenValue,
        });

      return scheduledDeliveryTimes;
    },
    {
      refreshInterval: 60 * 1000, // 1 minute
      revalidateOnFocus: true,
      revalidateOnMount: true,
      use: [laggy],
    },
  );

  return expectedPickupTimesResponse;
}
export function useCartExpectedDeliveryTime() {
  const { trpc } = useApp();
  const { data: cart } = useCart();

  const expectedDeliveryTimeResponse = useSWR(
    cart?.deliveryMethodType === 'courier'
      ? (['order.expectedDeliveryTime', cart.tokenValue] as const)
      : false,
    async ([_, tokenValue]) => {
      const expectedDeliveryTime = await trpc.order.expectedDeliveryTime.query({
        tokenValue,
      });

      return expectedDeliveryTime;
    },
    {
      refreshInterval: 60 * 1000, // 1 minute
      revalidateOnFocus: true,
      revalidateOnMount: true,
    },
  );

  return expectedDeliveryTimeResponse;
}

export function useCheckout(
  onCheckoutComplete?: (
    order: NonNullable<RouterOutputs['order']['byTokenValue']>,
  ) => void,
) {
  const { trpc } = useApp();
  const { mutate: globalMutate } = useSWRConfig();
  const [orderIdentifier, setOrderIdentifier] = useCartOrderIdentifier();
  const onCheckoutCompleteRef = useRef(onCheckoutComplete);
  onCheckoutCompleteRef.current = onCheckoutComplete;

  type CheckoutArg = Omit<RouterInputs['order']['checkout'], 'tokenValue'>;

  const { trigger, ...checkoutMutation } = useSWRMutation<
    Extract<RouterOutputs['order']['checkout'], { ok: true }>['data'],
    AppErrorType<
      Extract<RouterOutputs['order']['checkout'], { ok: false }>['error']
    >,
    ReturnType<typeof getCartKey>,
    CheckoutArg
  >(
    getCartKey(orderIdentifier),
    async (
      [_, tokenValue],
      {
        arg,
      }: {
        arg: CheckoutArg;
      },
    ) => {
      const result = await trpc.order.checkout.mutate({
        tokenValue,
        ...arg,
      });

      if (result.ok) {
        return result.data;
      } else {
        throw AppError.from(result.error);
      }
    },
    {
      populateCache: true,
    },
  );

  const { data: deliveryTime, mutate: refreshDeliveryTime } =
    useCartExpectedDeliveryTime();

  const deliveryTimeRef = useRef(deliveryTime);
  deliveryTimeRef.current = deliveryTime;

  const checkout = useCallback(
    async (data: v2_OrderCompleteFormData) => {
      return trigger(
        {
          contact: {
            firstName: data.shippingAddressFirstName,
            lastName: data.shippingAddressLastName,
            email: data.email,
            phoneNumber: data.shippingAddressPhoneNumber,
          },
          billingAddress: data.useDifferentBillingAddress
            ? {
                firstName: data.billingAddressFirstName,
                lastName: data.billingAddressLastName,
                phoneNumber: data.shippingAddressPhoneNumber,
                company: data.billingAddressCompany,
                crn: data.billingAddressCRN,
                vat: data.billingAddressVAT,
                street: data.billingAddressStreet,
                number: data.billingAddressNumber,
                city: data.billingAddressCity,
                postcode: data.billingAddressPostcode,
              }
            : undefined,
          payment: {
            paymentMethodCode: assertSome(data.paymentMethod),
            rememberCard: data.useRememberedCard,
          },
          expectedPickupTime: data.expectedPickupTime ?? null,
          notes: {
            courier: data.courierNote,
            kitchen: data.kitchenNote,
          },
          estimatedDeliveryTime:
            deliveryTimeRef.current?.value.toString() ?? null,
        },
        {
          onSuccess: (data) => {
            globalMutate(getOrderKey(data.tokenValue), data);

            onCheckoutCompleteRef.current?.(data);

            setOrderIdentifier(null);
          },
        },
      );
    },
    [trigger],
  );

  const {
    trigger: setSpecialDelivery,
    error: setSpecialDeliveryError,
    isMutating: setSpecialDeliveryIsMutating,
  } = useSWRMutation(
    getCartKey(orderIdentifier),
    async (
      [_, tokenValue],
      {
        arg: specialDelivery,
      }: {
        arg: RouterInputs['order']['setSpecialDelivery']['specialDelivery'];
      },
    ) => {
      await trpc.order.setSpecialDelivery.mutate({
        tokenValue,
        specialDelivery,
      });
    },
    {
      revalidate: true,
    },
  );

  const { data: paymentMethodsData } = useCartPaymentMethods();

  const paymentMethods = useMemo(() => {
    if (!paymentMethodsData) {
      return [];
    }

    return paymentMethodsData.map((paymentMethod) => ({
      value: paymentMethod.code,
      label: paymentMethod.name,
      description: paymentMethod.description,
      code: paymentMethod.code,
      maxOrderValue: paymentMethod.maxOrderValue,
    }));
  }, [paymentMethodsData]);

  const { data: pickupTimes, mutate: refreshPickupTimes } =
    useCartExpectedPickupTimes();
  const {
    data: scheduledDeliveryTimes,
    mutate: refreshScheduledDeliveryTimes,
  } = useCartScheduledDeliveryTimes();

  const { data: customer } = useAuthorizedCustomer();
  const { data: billingAddress } = useCustomerBillingAddress();

  const form = v2_useOrderCompleteForm({
    onSubmit: checkout,
    paymentMethods,
    pickupTimes: pickupTimes ?? [],
    scheduledDeliveryTimes: scheduledDeliveryTimes ?? [],
    customer,
    billingAddress,
  });

  return {
    form,
    paymentMethods,
    pickupTimes,
    refreshPickupTimes,
    deliveryTime,
    refreshDeliveryTime,
    scheduledDeliveryTimes,
    refreshScheduledDeliveryTimes,
    setSpecialDelivery,
    setSpecialDeliveryError,
    setSpecialDeliveryIsMutating,
    ...checkoutMutation,
  };
}

export function useInstantOrderCheckout(
  {
    product,
    filteredConfiguration,
    configurationValues,
  }: {
    product: ProductDetail | null | undefined;
    filteredConfiguration: ProductDetail['configuration'];
    configurationValues:
      | ProductDetailState['configurationValues']
      | null
      | undefined;
  },
  onCheckoutComplete?: (
    order: NonNullable<RouterOutputs['order']['byTokenValue']>,
  ) => void,
) {
  const { trpc, locale } = useApp();
  const onCheckoutCompleteRef = useRef(onCheckoutComplete);
  onCheckoutCompleteRef.current = onCheckoutComplete;

  const configurationValuesRef = useRef(configurationValues);
  configurationValuesRef.current = configurationValues;

  type CheckoutArg = RouterInputs['order']['instantOrder'];

  const { trigger, ...instantOrderMutation } = useSWRMutation<
    Extract<RouterOutputs['order']['instantOrder'], { ok: true }>['data'],
    AppErrorType<
      Extract<RouterOutputs['order']['instantOrder'], { ok: false }>['error']
    >,
    readonly ['order.instantOrder'],
    CheckoutArg
  >(
    ['order.instantOrder'] as const,
    async (
      _,
      {
        arg,
      }: {
        arg: CheckoutArg;
      },
    ) => {
      const result = await trpc.order.instantOrder.mutate({
        ...arg,
      });

      if (result.ok) {
        return result.data;
      } else {
        throw AppError.from(result.error);
      }
    },
    {
      populateCache: true,
    },
  );

  const placeOrder = useCallback(
    async (data: v2_OrderCompleteFormData) => {
      if (!product || !configurationValuesRef.current) {
        return;
      }

      return trigger(
        {
          configuration: configurationValuesAsAddToCardPayload(
            product,
            configurationValuesRef.current,
          ),
          locale,
          contact: {
            firstName: data.shippingAddressFirstName,
            lastName: data.shippingAddressLastName,
            email: data.email,
            phoneNumber: data.shippingAddressPhoneNumber,
          },
          billingAddress: data.useDifferentBillingAddress
            ? {
                firstName: data.billingAddressFirstName,
                lastName: data.billingAddressLastName,
                phoneNumber: data.shippingAddressPhoneNumber,
                company: data.billingAddressCompany,
                crn: data.billingAddressCRN,
                vat: data.billingAddressVAT,
                street: data.billingAddressStreet,
                number: data.billingAddressNumber,
                city: data.billingAddressCity,
                postcode: data.billingAddressPostcode,
              }
            : undefined,
          payment: {
            paymentMethodCode: assertSome(data.paymentMethod),
            rememberCard: data.useRememberedCard,
          },
        },
        {
          onSuccess: (data) => {
            onCheckoutCompleteRef.current?.(data);
          },
        },
      );
    },
    [trigger, locale, product],
  );

  const { data: paymentMethodsData } = usePaymentMethods();

  const paymentMethods = useMemo(() => {
    if (!paymentMethodsData) {
      return [];
    }

    return paymentMethodsData.map((paymentMethod) => ({
      value: paymentMethod.code,
      label: paymentMethod.name,
      description: paymentMethod.description,
      code: paymentMethod.code,
      maxOrderValue: paymentMethod.maxOrderValue,
    }));
  }, [paymentMethodsData]);

  const { data: customer } = useAuthorizedCustomer();
  const { data: billingAddress } = useCustomerBillingAddress();

  const form = v2_useOrderCompleteForm({
    onSubmit: placeOrder,
    paymentMethods,
    pickupTimes: [],
    scheduledDeliveryTimes: [],
    customer,
    billingAddress,
  });

  const configuration = product?.configuration;

  const totalPrice = useMemo(() => {
    if (!configuration || !configurationValues) {
      return undefined;
    }

    return calculateConfigurationPrice(configuration, configurationValues);
  }, [configuration, configurationValues]);

  const configurationValidationErrors = useMemo(() => {
    if (
      product?.configurationStyle === undefined ||
      product?.maxSelectedConfigurationOptions === undefined ||
      !filteredConfiguration ||
      !configurationValues
    ) {
      return [];
    }

    return validateProductConfiguration(
      {
        configurationStyle: product?.configurationStyle,
        maxSelectedConfigurationOptions:
          product?.maxSelectedConfigurationOptions,
        filteredConfiguration,
      },
      configurationValues,
    );
  }, [
    product?.configurationStyle,
    product?.maxSelectedConfigurationOptions,
    filteredConfiguration,
    configurationValues,
  ]);

  return {
    form,
    paymentMethods,
    totalPrice,
    configurationValidationErrors,
    canPlaceOrder:
      Boolean(product?.available) &&
      configurationValidationErrors?.length === 0,
    ...instantOrderMutation,
  };
}

export type PaymentMethod = RouterOutputs['order']['paymentMethods'][number];

export function useCartApplyCoupon(
  cfg?: SWRMutationConfiguration<
    | Extract<RouterOutputs['order']['applyCoupon'], { ok: true }>['data']
    | undefined,
    AppErrorType<
      Extract<RouterOutputs['order']['applyCoupon'], { ok: false }>['error']
    >,
    string,
    CartKey
  >,
) {
  const { trpc } = useApp();
  const [orderIdentifier] = useCartOrderIdentifier();

  const {
    trigger: triggerMutation,
    error,
    isMutating,
  } = useSWRMutation<
    | Extract<RouterOutputs['order']['applyCoupon'], { ok: true }>['data']
    | undefined,
    AppErrorType<
      Extract<RouterOutputs['order']['applyCoupon'], { ok: false }>['error']
    >,
    CartKey,
    string
  >(
    getCartKey(orderIdentifier),
    async ([_, tokenValue], { arg }) => {
      const applyCouponResult = await trpc.order.applyCoupon.mutate({
        tokenValue,
        couponCode: arg,
      });

      if (applyCouponResult.ok) {
        return applyCouponResult.data;
      } else {
        throw AppError.from(applyCouponResult.error);
      }
    },
    {
      ...cfg,
      populateCache: true,
    },
  );

  const lockRef = useRef(false);
  const trigger = useCallback(
    (...args: Parameters<typeof triggerMutation>) => {
      if (lockRef.current) {
        return;
      }

      lockRef.current = true;

      try {
        triggerMutation(...args);
      } finally {
        lockRef.current = false;
      }
    },
    [triggerMutation],
  );

  return { trigger, error: error, isMutating };
}

interface AddToCartBuffer {
  readonly bufferedItem: Maybe<
    RouterInputs['order']['addItem']['configuration']
  >;
  readonly clearedState: Maybe<'orderCreated' | 'bufferCleared'>;
  readonly error: Maybe<
    AppError<Extract<RouterOutputs['order']['addItem'], { ok: false }>['error']>
  >;
  buffer(
    configuration: RouterInputs['order']['addItem']['configuration'],
  ): void;
  clear(orderCreated: boolean): void;
  setError(
    error: AppError<
      Extract<RouterOutputs['order']['addItem'], { ok: false }>['error']
    >,
  ): void;
}

const useAddToCartBuffer = createStore<AddToCartBuffer>((set) => ({
  bufferedItem: null,
  clearedState: null,
  error: null,
  buffer(configuration) {
    set({ bufferedItem: configuration, clearedState: null, error: null });
  },
  clear(orderCreated: boolean) {
    set({
      bufferedItem: null,
      clearedState: orderCreated ? 'orderCreated' : 'bufferCleared',
      error: null,
    });
  },
  setError(
    error: AppError<
      Extract<RouterOutputs['order']['addItem'], { ok: false }>['error']
    >,
  ) {
    set({
      error,
    });
  },
}));

export function useShippingSelectionModal() {
  const [bufferedItem, clear] = useAddToCartBuffer(
    (state) => [state.bufferedItem, state.clear],
    shallow,
  );

  const close = useCallback(() => {
    clear(false);
  }, [clear]);

  return {
    show: Boolean(bufferedItem),
    close,
  };
}

function isOrderCreatedWhenBufferCleared() {
  return new Promise<boolean>((resolve) => {
    let unsubscribe = noop;

    const onChange = (state: AddToCartBuffer) => {
      if (isSome(state.clearedState)) {
        unsubscribe();
        resolve(state.clearedState === 'orderCreated');
      }
    };

    unsubscribe = useAddToCartBuffer.subscribe(onChange);
  });
}
